import React from "react";
import { Col, FormControl, FormGroup, FormLabel, Row } from "react-bootstrap";
import { Link, Navigate, useParams } from "react-router-dom";
import { InputBox, Loading } from "../components";
import { ApiHelper, EnvironmentHelper, FeedInterface, PendingCommand, UserHelper, ToastHelper } from "../helpers";
import { Questions } from "./components";
import { ImageEditor } from "../components";
import { FileHelper } from "../helpers/FileHelper";

export const FeedAdminPage = () => {
  const { id } = useParams<{ id: string }>();
  const [feed, setFeed] = React.useState<FeedInterface>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [questionsCommand, setQuestionsCommand] = React.useState<PendingCommand>(null);
  const [redirect, setRedirect] = React.useState("");
  const [imageEditData, setImageEditData] = React.useState<string>(null);
  const [imageUrl, setImageUrl] = React.useState<string>(null);

  const init = () => {
    if (id === "0") {
      setFeed({
        id: 0, active: true, additionalScreenFee: 0, description: "", isPublic: false, lastModified: new Date(), monthlyAccountFee: 10, mode: "Playlist",
        name: "New Feed", shapes: "16:9", trialDays: 30, requiresApproval: false, accountId: UserHelper.currentDepartment.accountId
      });
    }
    else {
      ApiHelper.get<FeedInterface>("/feeds/" + id).then((data) => {
        setFeed(data)
        setImageUrl(EnvironmentHelper.ContentRoot + "/feeds/" + data?.id + "/thumb.png")
      });
    }
    setIsSubmitting(false);
    if (imageEditData) setImageEditData(null);
  }

  React.useEffect(init, [id]); //eslint-disable-line

  const handleImageUpdate = (dataUrl: string) => {
    if (dataUrl) {
      setImageUrl(dataUrl);
      setImageEditData(null);
    }
    setImageEditData(null);
  }


  const getImageEditor = () => {
    if (imageEditData) {
      let aspectRatio = 16 / 9;
      return <ImageEditor startingUrl={imageUrl} updatedFunction={handleImageUpdate} name="imageEditor" aspectRatio={aspectRatio} />
    }
  }


  const handleChange = (e: any) => {
    let f: FeedInterface = { ...feed };
    let val = e.currentTarget.value;
    let numericVal = 0;
    try {
      numericVal = parseFloat(val.replace("$", "").replace(",", ""))
    } catch { }
    switch (e.currentTarget.id) {
      case "name": f.name = val; break;
      case "isPublic": f.isPublic = val === "true"; break;
      case "mode": f.mode = val; break;
      case "shapes": f.shapes = val; break;
      case "description": f.description = val; break;
      case "monthlyAccountFee": f.monthlyAccountFee = numericVal; break;
      case "additionalScreenFee": f.additionalScreenFee = numericVal; break;
      case "trialDays": f.trialDays = numericVal; break;
    }
    setFeed(f);
  }

  const validate = () => {
    if (feed.name === "") {
      ToastHelper.error("Please enter a feed name.");
      return false;
    }
    return true;
  }

  const handleSave = async () => {
    if (validate()) {
      setIsSubmitting(true);
      const f = { ...feed };
      f.lastModified = new Date();
      const data = await ApiHelper.post<FeedInterface[]>("/feeds", [f]);
      if (imageUrl?.startsWith("data:image/")) await FileHelper.preUpload("feed", data[0].id, 0, FileHelper.dataURLtoFile(imageUrl, "thumb.png"), "thumb.png", () => { });
      setQuestionsCommand({ action: "save", parameter: { feedId: data[0].id, templateId: null } });
    }
  }

  const getDeleteFunction = () => (feed?.id > 0) ? handleDelete : undefined

  const handleDelete = () => {
    if (window.confirm("Are you sure you wish to permanently delete this feed?")) {
      ApiHelper.delete("/feeds/" + feed.id).then(() => {
        setRedirect("/settings/feedsAdmin");
      });
    }
  }


  const questionsSaved = () => {
    setIsSubmitting(false);
    ToastHelper.success("Feed saved successfully");
  }


  const getQuestions = () => {
    return <Questions feedId={feed?.id} saveCallback={questionsSaved} pendingCommand={questionsCommand} />
    //return <MessageAnswers message={message} pendingCommand={answersCommand} saveCallback={answersSaved} sendCommand={handleAnswersCommand} />
  }

  const showEditor = (e: React.MouseEvent) => {
    e.preventDefault();
    setImageEditData(imageUrl);
  }

  const getHeaderButtons = () => {
    const result: React.ReactElement[] = [];
    if (feed?.libraryPlaylistId && (feed?.mode === "Library" || feed?.mode === "Both")) {
      result.push(<Link to={"/playlists/" + feed.libraryPlaylistId + "?mode=library&feedId=" + feed.id} className="btn btn-sm btn-success"><i className="fas fa-edit"></i> Library</Link>);
    }
    if (feed?.playlistId && (feed?.mode === "Playlist" || feed?.mode === "Both")) {
      result.push(<Link to={"/playlists/" + feed.playlistId + "?mode=playlist&feedId=" + feed.id} className="btn btn-sm btn-success" style={{ marginLeft: 10 }}><i className="fas fa-edit"></i> Playlist</Link>);
    }
    return <div>{result}</div>;
  }


  const formatter = Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

  if (redirect !== "") return <Navigate to={redirect} replace />
  else if (!feed) return <Loading />
  else return (
    <>
      <h1><i className="fas fa-satellite-dish"></i> Edit Feed</h1>
      <Row>
        <Col md={6}>
          <InputBox headerIcon="fas fa-satellite-dish" headerText="Edit Feed" saveFunction={handleSave} isSubmitting={isSubmitting} deleteFunction={getDeleteFunction()} headerActionContent={getHeaderButtons()} >
            <Row>
              <Col>
                <FormGroup className="mb-3">
                  <FormLabel htmlFor="name">Name</FormLabel>
                  <FormControl id="name" value={feed.name} onChange={handleChange} />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup className="mb-3">
                  <FormLabel>Is Public</FormLabel>
                  <FormControl as="select" id="isPublic" value={feed.isPublic.toString()} onChange={handleChange}>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </FormControl>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col>
                <FormGroup className="mb-3">
                  <FormLabel>Mode</FormLabel>
                  <FormControl as="select" id="mode" value={feed.mode} onChange={handleChange}>
                    <option value="Playlist">Playlist</option>
                    <option value="Library">Library</option>
                    <option value="Both">Both</option>
                  </FormControl>
                </FormGroup>
              </Col>
              <Col>
                <FormGroup className="mb-3">
                  <FormLabel>Shapes</FormLabel>
                  <FormControl as="select" id="shapes" value={feed.shapes} onChange={handleChange}>
                    <option value="16:9">Horizontal</option>
                    <option value="9:16">Vertical</option>
                    <option value="16:9,9:16">Both</option>
                  </FormControl>
                </FormGroup>
              </Col>
            </Row>
            <FormGroup className="mb-3">
              <FormLabel>Description</FormLabel>
              <FormControl as="textarea" id="description" value={feed.description} onChange={handleChange} rows={5} />
            </FormGroup>
            <Row>
              <Col>
                <FormGroup className="mb-3">
                  <FormLabel htmlFor="name">Monthly Fee</FormLabel>
                  <FormControl id="monthlyAccountFee" value={formatter.format(feed.monthlyAccountFee)} onChange={handleChange} />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup className="mb-3">
                  <FormLabel htmlFor="name">Per Screen Fee</FormLabel>
                  <FormControl id="additionalScreenFee" value={formatter.format(feed.additionalScreenFee)} onChange={handleChange} />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup className="mb-3">
                  <FormLabel htmlFor="name">Trial Days</FormLabel>
                  <FormControl id="trialDays" value={feed.trialDays} onChange={handleChange} />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup className="mb-3">
              <FormLabel>Thumbnail</FormLabel>
              <div>
                <a href="about:blank" onClick={showEditor} ><img src={imageUrl} alt="thumbnail" style={{ maxWidth: 150 }} /></a>
              </div>
            </FormGroup>
          </InputBox >
        </Col>
        <Col md={6}>
          {getImageEditor()}
          {getQuestions()}
        </Col>
      </Row>
    </>
  );

};